'use client';

import * as React from 'react';
import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { useRouter } from 'next/navigation';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import SmallCustomCalendar from '../small_custom_calendar';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { trackEvent } from '../../lib/heapClient';

// Create theme for MUI components
const theme = createTheme({
    palette: {
        primary: {
            main: '#C0C0DD',
        },
        background: {
            paper: '#1A1B23',
        },
        text: {
            primary: '#FFFFFF',
        },
    },
    typography: {
        // Increase typography sizes by ~15% for the calendar
        subtitle1: {
            fontSize: '1.15rem',
        },
        caption: {
            fontSize: '0.93rem',
        },
        body2: {
            fontSize: '1.1rem',
        }
    },
    components: {
        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: '8px', // Slightly larger click targets
                }
            }
        }
    }
});

interface SearchPillProps {
    className?: string;
    defaultDate?: Date;
    defaultTime?: string;
    defaultPartySize?: string;
    defaultLocation?: string;
    defaultEventType?: string;
}

// DatePicker component - Memoized
const DatePicker = memo(({ date, setDate, onClose }: { date: Date | null, setDate: (date: Date) => void, onClose: () => void }) => {
    // Handle date selection
    const handleDateChange = useCallback((newDate: Date) => {
        console.log('Date selected:', newDate);
        try {
            setDate(newDate);
            // Track date selection event
            trackEvent('mobile_homepage_search_date_selected', {
                date: newDate.toISOString().split('T')[0],
                component: 'SearchPill'
            });
            // Close the calendar after setting the date
            setTimeout(() => onClose(), 50);
        } catch (error) {
            console.error('Error in date selection:', error);
        }
    }, [setDate, onClose]);

    // Component wrapper to prevent click propagation
    const calendarWrapperRef = useRef<HTMLDivElement>(null);

    // Handle clicks on the calendar wrapper to prevent closing
    const handleCalendarClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const calendar = (
        <div
            className="fixed inset-0 z-[9999]"
            onClick={(e) => {
                // Only close if clicking the backdrop (not the calendar)
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="absolute top-[50%] left-[50%] transform translate-x-[-50%] translate-y-[-50%] calendar-wrapper"
                ref={calendarWrapperRef}
                onClick={handleCalendarClick}
                style={{ transform: 'translate(-50%, -50%) scale(1.4)' }} // Scale up by 30%
            >
                <ThemeProvider theme={theme}>
                    <div className="homepage-calendar-wrapper bg-[#1A1B23] shadow-lg">
                        <SmallCustomCalendar
                            selectedDate={date || new Date()}
                            onDateChange={handleDateChange}
                            minDate={new Date()} // Set minimum date to today
                            isHomepage={true}
                        />
                    </div>
                </ThemeProvider>
            </div>
        </div>
    );

    // Use createPortal to render the calendar directly to the document body
    return typeof document !== 'undefined' ? createPortal(calendar, document.body) : null;
});

// TimePicker component - Memoized
const TimePicker = memo(({ time, setTime, onClose }: { time: string, setTime: (time: string) => void, onClose: () => void }) => {
    const handleTimePickerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent clicks inside the time picker from closing it
    };

    const handleTimeSelect = (timeStr: string) => {
        console.log('Time selected:', timeStr);
        setTime(timeStr);
        // Track time selection event
        trackEvent('mobile_homepage_search_time_selected', {
            time: timeStr,
            component: 'SearchPill'
        });
        setTimeout(() => onClose(), 10);
    };

    // Generate all possible times with 30-minute intervals
    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute of [0, 30]) {
                const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                times.push({ hour, minute, timeStr });
            }
        }
        return times;
    };

    const timeOptions = generateTimeOptions();

    const timePickerContent = (
        <div
            className="fixed top-[50%] left-[50%] transform translate-x-[-50%] translate-y-[-50%] z-[9999] w-[250px] bg-[#1A1B23] p-4 rounded-2xl shadow-lg border-[1.5px] border-[rgba(224,177,241,0.2)] time-picker-container"
            style={{ backgroundColor: "#1A1B23" }}
            onClick={handleTimePickerClick}
        >
            <div className="flex items-center justify-center mb-3">
                <div className="text-white font-semibold text-lg">Select Time</div>
            </div>
            <div className="max-h-[300px] overflow-y-auto pr-1">
                <ul className="w-full">
                    {timeOptions.map(({ hour, minute, timeStr }) => {
                        const isSelected = time === timeStr;
                        return (
                            <li
                                key={timeStr}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleTimeSelect(timeStr);
                                }}
                                className={`
                                    w-full py-2 px-4 cursor-pointer mb-1 rounded-lg text-center
                                    hover:bg-[rgba(224,177,241,0.2)] transition-colors
                                    ${isSelected
                                        ? 'bg-[#C0C0DD] text-[#1C1C34] font-bold shadow-sm'
                                        : 'text-white'}
                                `}
                            >
                                {new Date(0, 0, 0, hour, minute).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );

    // Use createPortal to render directly to the document body
    return typeof document !== 'undefined' ? createPortal(timePickerContent, document.body) : null;
});

// PartySizePicker component - Memoized
const PartySizePicker = memo(({ partySize, setPartySize, onClose }: { partySize: string, setPartySize: (size: string) => void, onClose: () => void }) => {
    const [tempPartySize, setTempPartySize] = useState(partySize);

    // Sync tempPartySize if partySize changes
    useEffect(() => {
        setTempPartySize(partySize);
    }, [partySize]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '' || /^\d+$/.test(value)) {
            setTempPartySize(value);
        }
    };

    const handleSave = () => {
        if (tempPartySize && parseInt(tempPartySize) > 0) {
            console.log('Saving party size:', tempPartySize);
            setPartySize(tempPartySize);
            // Track party size selection event
            trackEvent('mobile_homepage_search_party_size_selected', {
                party_size: tempPartySize,
                component: 'SearchPill'
            });
            setTimeout(() => onClose(), 10);
        } else {
            // Reset to original value if invalid
            setTempPartySize(partySize);
            onClose();
        }
    };

    // Handle enter key
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSave();
        }
    };

    const handlePartySizePickerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent clicks inside the picker from closing it
    };

    const partySizePickerContent = (
        <div
            className="fixed top-[50%] left-[50%] transform translate-x-[-50%] translate-y-[-50%] z-[9999] w-[250px] bg-[#1A1B23] p-4 rounded-2xl shadow-lg border-[1.5px] border-[rgba(224,177,241,0.2)]"
            style={{ backgroundColor: "#1A1B23" }}
            onClick={handlePartySizePickerClick}
        >
            <div className="text-white font-semibold text-lg mb-3 text-center">Guest Count</div>
            <input
                type="text"
                value={tempPartySize}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                className="w-full mb-3 bg-[#1C1C34]/60 border border-[#C0C0DD]/30 rounded-xl px-3 py-2 text-center text-white text-lg focus:outline-none focus:border-[#C0C0DD]"
                pattern="[0-9]*"
                inputMode="numeric"
                autoFocus
            />
            <div className="flex justify-between gap-2">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onClose();
                    }}
                    className="flex-1 border border-gray-600 text-gray-400 py-2 rounded-md text-sm hover:bg-gray-800"
                >
                    Cancel
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleSave();
                    }}
                    className="flex-1 bg-[#C0C0DD] text-[#1C1C34] py-2 rounded-md text-sm font-semibold hover:bg-[#d8a8e8]"
                >
                    Apply
                </button>
            </div>
        </div>
    );

    // Use createPortal to render directly to the document body
    return typeof document !== 'undefined' ? createPortal(partySizePickerContent, document.body) : null;
});

// LocationPicker component - Memoized
const LocationPicker = memo(({ location, setLocation, onClose }: { location: string, setLocation: (location: string) => void, onClose: () => void }) => {
    console.log('LocationPicker rendering with location:', location);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLocations, setSelectedLocations] = useState<string[]>(location ? location.split(',') : []);

    // Sync selectedLocations if location changes
    useEffect(() => {
        setSelectedLocations(location ? location.split(',') : []);
    }, [location]);

    // NYC neighborhoods
    const neighborhoods = [
        'Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island',
        'East Village', 'West Village', 'SoHo', 'Chelsea', 'Tribeca',
        'Upper East Side', 'Upper West Side', 'Midtown', 'Financial District',
        'Lower East Side', 'Harlem', 'Williamsburg', 'DUMBO', 'Bushwick',
        'Greenpoint', 'Astoria', 'Long Island City'
    ];

    const filteredNeighborhoods = searchTerm
        ? neighborhoods.filter(n => n.toLowerCase().includes(searchTerm.toLowerCase()))
        : neighborhoods;

    const handleSelect = (neighborhood: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const isSelected = selectedLocations.includes(neighborhood);

        if (isSelected) {
            setSelectedLocations(selectedLocations.filter(n => n !== neighborhood));
        } else {
            setSelectedLocations([...selectedLocations, neighborhood]);
        }
    };

    const handleSave = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        console.log('Saving locations:', selectedLocations);
        setLocation(selectedLocations.join(','));
        // Track location selection event
        trackEvent('mobile_homepage_search_location_selected', {
            locations: selectedLocations,
            count: selectedLocations.length,
            component: 'SearchPill'
        });
        setTimeout(() => onClose(), 10);
    };

    const handleCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        setSearchTerm(e.target.value);
    };

    const handleLocationPickerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent clicks inside the picker from closing it
    };

    const locationPickerContent = (
        <div
            className="fixed top-[50%] left-[50%] transform translate-x-[-50%] translate-y-[-50%] z-[9999] w-[300px] bg-[#1A1B23] p-4 rounded-2xl shadow-lg border-[1.5px] border-[rgba(224,177,241,0.2)] location-picker-container"
            style={{ backgroundColor: "#1A1B23" }}
            onClick={handleLocationPickerClick}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="text-white font-semibold text-lg">Where do you want to party?</div>
            </div>
            <input
                type="text"
                placeholder="Search neighborhoods"
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
                className="w-full mb-3 bg-[#1C1C34]/60 border border-[#C0C0DD]/30 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C0C0DD]"
            />
            <div className="max-h-[200px] overflow-y-auto mb-3">
                <div className="flex flex-wrap gap-2">
                    {filteredNeighborhoods.map(neighborhood => (
                        <button
                            key={neighborhood}
                            onClick={(e) => handleSelect(neighborhood, e)}
                            className={`
              py-1 px-3 rounded-full text-xs
              ${selectedLocations.includes(neighborhood)
                                    ? 'bg-[#C0C0DD] text-[#1C1C34] font-bold'
                                    : 'text-white border border-[rgba(224,177,241,0.6)] hover:bg-[rgba(224,177,241,0.1)]'}
            `}
                        >
                            {neighborhood}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex justify-between">
                <button
                    onClick={handleCancel}
                    className="border border-gray-600 text-gray-400 px-3 py-1 rounded-md text-sm hover:bg-gray-800"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="bg-[#C0C0DD] text-[#1C1C34] px-3 py-1 rounded-md text-sm font-semibold hover:bg-[#d8a8e8]"
                >
                    Apply
                </button>
            </div>
        </div>
    );

    // Use createPortal to render directly to the document body
    return typeof document !== 'undefined' ? createPortal(locationPickerContent, document.body) : null;
});

// EventTypePicker component - Memoized
const EventTypePicker = memo(({ eventType, setEventType, onClose }: { eventType: string, setEventType: (type: string) => void, onClose: () => void }) => {
    console.log('EventTypePicker rendering with eventType:', eventType);
    const [selectedTypes, setSelectedTypes] = useState<string[]>(eventType ? eventType.split(',') : []);

    // Sync selectedTypes if eventType changes
    useEffect(() => {
        setSelectedTypes(eventType ? eventType.split(',') : []);
    }, [eventType]);

    // Event types matching those in homepage_pill.tsx
    const eventTypes = [
        'wedding', 'corporate', 'no minimum spend', 'bar', 'restaurant', 'fancy', 'casual',
        'intimate gathering'
    ];

    const handleSelect = (type: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const isSelected = selectedTypes.includes(type);

        if (isSelected) {
            setSelectedTypes(selectedTypes.filter(t => t !== type));
        } else {
            setSelectedTypes([...selectedTypes, type]);
        }
    };

    const handleSave = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        console.log('Saving event types:', selectedTypes);
        setEventType(selectedTypes.join(','));
        // Track event type selection event
        trackEvent('mobile_homepage_search_event_type_selected', {
            event_types: selectedTypes,
            count: selectedTypes.length,
            component: 'SearchPill'
        });
        setTimeout(() => onClose(), 10);
    };

    const handleCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    };

    const handleEventTypePickerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent clicks inside the picker from closing it
    };

    const eventTypePickerContent = (
        <div
            className="fixed top-[50%] left-[50%] transform translate-x-[-50%] translate-y-[-50%] z-[9999] w-[300px] bg-[#1A1B23] p-4 rounded-2xl shadow-lg border-[1.5px] border-[rgba(224,177,241,0.2)] event-type-picker-container"
            style={{ backgroundColor: "#1A1B23" }}
            onClick={handleEventTypePickerClick}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="text-white font-semibold text-lg">What's your vibe?</div>
            </div>
            <div className="max-h-[200px] overflow-y-auto mb-3">
                <div className="flex flex-wrap gap-2">
                    {eventTypes.map(type => (
                        <button
                            key={type}
                            onClick={(e) => handleSelect(type, e)}
                            className={`
              py-1 px-3 rounded-full text-xs capitalize
              ${selectedTypes.includes(type)
                                    ? 'bg-[#C0C0DD] text-[#1C1C34] font-bold'
                                    : 'text-white border border-[rgba(224,177,241,0.6)] hover:bg-[rgba(224,177,241,0.1)]'}
            `}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex justify-between">
                <button
                    onClick={handleCancel}
                    className="border border-gray-600 text-gray-400 px-3 py-1 rounded-md text-sm hover:bg-gray-800"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="bg-[#C0C0DD] text-[#1C1C34] px-3 py-1 rounded-md text-sm font-semibold hover:bg-[#d8a8e8]"
                >
                    Apply
                </button>
            </div>
        </div>
    );

    // Use createPortal to render directly to the document body
    return typeof document !== 'undefined' ? createPortal(eventTypePickerContent, document.body) : null;
});

// SearchFormField component - Memoized
const SearchFormField = memo(({
    label,
    value,
    isEditing,
    onClick,
    children
}: {
    label: string,
    value: string,
    isEditing: boolean,
    onClick: (e?: React.MouseEvent) => void,
    children?: React.ReactNode
}) => {
    const handleClick = (e: React.MouseEvent) => {
        console.log(`SearchFormField ${label} clicked`);
        // Prevent propagation to parent elements
        e.stopPropagation();
        // Call the passed onClick handler with the event
        onClick(e);
    };

    return (
        <div
            onClick={handleClick}
            className={`flex flex-col cursor-pointer p-1 h-14 rounded-lg border border-transparent transition-all duration-150 ${isEditing ? 'bg-[#C0C0DD] border-[#1C1C34]' : 'hover:bg-[#C0C0DD]'}`}
            style={{ backgroundColor: isEditing ? "#C0C0DD" : "transparent" }}
        >
            <div className="text-sm font-bold text-black text-center">{label}</div>
            <div className="text-center text-base font-medium text-gray-700 truncate">
                {value}
            </div>
            {children}
        </div>
    );
});

const SearchPill: React.FC<SearchPillProps> = ({
    className = '',
    defaultDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    defaultTime = '19:00',
    defaultPartySize = '25',
    defaultLocation = '',
    defaultEventType = '',
}) => {
    const router = useRouter();

    // Field values
    const [date, setDate] = useState<Date | null>(defaultDate);
    const [time, setTime] = useState<string>(defaultTime);
    const [partySize, setPartySize] = useState<string>(defaultPartySize);
    const [location, setLocation] = useState<string>(defaultLocation);
    const [eventType, setEventType] = useState<string>(defaultEventType);

    // Field editing states
    const [dateEditing, setDateEditing] = useState<boolean>(false);
    const [timeEditing, setTimeEditing] = useState<boolean>(false);
    const [peopleEditing, setPeopleEditing] = useState<boolean>(false);
    const [locationEditing, setLocationEditing] = useState<boolean>(false);
    const [eventTypeEditing, setEventTypeEditing] = useState<boolean>(false);

    // Refs for click outside handling
    const formRef = useRef<HTMLFormElement>(null);
    const dateInputRef = useRef<HTMLDivElement>(null);
    const peopleInputRef = useRef<HTMLDivElement>(null);
    const locationInputRef = useRef<HTMLDivElement>(null);
    const eventTypeInputRef = useRef<HTMLDivElement>(null);

    // Event types
    const eventTypes = [
        'Restaurant', 'Bar', 'Lounge', 'Club', 'Rooftop',
        'Private Dining', 'Wedding', 'Corporate', 'Birthday',
        'Cocktail Bar', 'Brewery', 'Art Gallery'
    ];

    // Handle date selection
    const handleDateChange = useCallback((newDate: Date) => {
        try {
            console.log('Setting new date in SearchPill:', newDate);
            // Make sure we have a valid date
            if (newDate instanceof Date && !isNaN(newDate.getTime())) {
                setDate(newDate);
                // Ensure the dateEditing is set to false after date is selected
                setTimeout(() => setDateEditing(false), 50);
            } else {
                console.error('Invalid date selected:', newDate);
            }
        } catch (error) {
            console.error('Error setting date:', error);
        }
    }, []);

    // Create a dedicated handler for party size updates
    const handlePartySizeChange = useCallback((newSize: string) => {
        try {
            console.log('Setting new party size:', newSize);
            if (newSize && /^\d+$/.test(newSize) && parseInt(newSize) > 0) {
                setPartySize(newSize);
                // Close the editor after a successful update
                setTimeout(() => setPeopleEditing(false), 50);
            } else {
                console.error('Invalid party size:', newSize);
            }
        } catch (error) {
            console.error('Error setting party size:', error);
        }
    }, []);

    // Create a dedicated handler for location updates
    const handleLocationChange = useCallback((newLocation: string) => {
        try {
            console.log('Setting new location:', newLocation);
            setLocation(newLocation);
            // Close the editor after a successful update
            setTimeout(() => setLocationEditing(false), 50);
        } catch (error) {
            console.error('Error setting location:', error);
        }
    }, []);

    // Create a dedicated handler for time updates
    const handleTimeChange = useCallback((newTime: string) => {
        try {
            console.log('Setting new time:', newTime);
            setTime(newTime);
            // Close the editor after a successful update
            setTimeout(() => setTimeEditing(false), 50);
        } catch (error) {
            console.error('Error setting time:', error);
        }
    }, []);

    // Create a dedicated handler for eventType updates
    const handleEventTypeChange = useCallback((newType: string) => {
        try {
            console.log('Setting new event type:', newType);
            setEventType(newType);
            // Close the editor after a successful update
            setTimeout(() => setEventTypeEditing(false), 50);
        } catch (error) {
            console.error('Error setting event type:', error);
        }
    }, []);

    // Handle click outside to close any open editors - using the same approach as homepage_pill.tsx
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Date picker handling
            if (dateEditing &&
                dateInputRef.current &&
                !dateInputRef.current.contains(event.target as Node) &&
                !(event.target as Element).closest('.calendar-wrapper')) {
                console.log('Click outside date picker detected');
                setDateEditing(false);
            }

            // Time picker handling
            if (timeEditing && !(event.target as Element).closest('.time-picker-container')) {
                setTimeEditing(false);
            }

            // Party size picker handling
            if (peopleEditing &&
                peopleInputRef.current &&
                !peopleInputRef.current.contains(event.target as Node)) {
                setPeopleEditing(false);
            }

            // Location picker handling
            if (locationEditing &&
                locationInputRef.current &&
                !locationInputRef.current.contains(event.target as Node) &&
                !(event.target as Element).closest('.location-picker-container')) {
                console.log('Click outside location picker detected');
                setLocationEditing(false);
            }

            // Event type picker handling
            if (eventTypeEditing &&
                eventTypeInputRef.current &&
                !eventTypeInputRef.current.contains(event.target as Node) &&
                !(event.target as Element).closest('.event-type-picker-container')) {
                console.log('Click outside event type picker detected');
                setEventTypeEditing(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dateEditing, timeEditing, peopleEditing, locationEditing, eventTypeEditing]);

    const handleSearch = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        const searchParams = new URLSearchParams();

        if (date) searchParams.set('date', date.toISOString().split('T')[0]);
        if (time) searchParams.set('time', time);
        if (partySize) searchParams.set('size', partySize);
        if (location) searchParams.set('neighborhoods', location);
        if (eventType) searchParams.set('type', eventType);

        // Track search submission event
        trackEvent('mobile_homepage_search_submitted', {
            component: 'SearchPill',
            date: date ? date.toISOString().split('T')[0] : null,
            time: time,
            party_size: partySize,
            locations: location ? location.split(',') : [],
            event_types: eventType ? eventType.split(',') : [],
            search_params: searchParams.toString()
        });

        router.push(`/search?${searchParams.toString()}`);
    }, [date, time, partySize, location, eventType, router]);

    // Handlers for each field - follow homepage_pill.tsx approach with clear state management
    const handleDateClick = useCallback((e?: React.MouseEvent) => {
        console.log('Date clicked');

        // Close other editors first
        setTimeEditing(false);
        setPeopleEditing(false);
        setLocationEditing(false);
        setEventTypeEditing(false);

        // Then toggle this editor with a slight delay
        setTimeout(() => {
            setDateEditing(prev => !prev);
        }, 10);
    }, []);

    const handleTimeClick = useCallback((e?: React.MouseEvent) => {
        setDateEditing(false);
        setPeopleEditing(false);
        setLocationEditing(false);
        setEventTypeEditing(false);

        setTimeout(() => {
            setTimeEditing(true);
        }, 10);
    }, []);

    const handlePeopleClick = useCallback((e?: React.MouseEvent) => {
        setDateEditing(false);
        setTimeEditing(false);
        setLocationEditing(false);
        setEventTypeEditing(false);

        setTimeout(() => {
            setPeopleEditing(true);
        }, 10);
    }, []);

    const handleLocationClick = useCallback((e?: React.MouseEvent) => {
        console.log('Location clicked');
        setDateEditing(false);
        setTimeEditing(false);
        setPeopleEditing(false);
        setEventTypeEditing(false);

        setTimeout(() => {
            setLocationEditing(true);
        }, 10);
    }, []);

    const handleEventTypeClick = useCallback((e?: React.MouseEvent) => {
        console.log('Event type clicked');
        setDateEditing(false);
        setTimeEditing(false);
        setPeopleEditing(false);
        setLocationEditing(false);

        setTimeout(() => {
            setEventTypeEditing(true);
        }, 10);
    }, []);

    return (
        <form
            ref={formRef}
            onSubmit={handleSearch}
            role="search"
            aria-label="Find venues"
            className={`w-full bg-[#C0C0DD] search-form px-2 py-1 z-[50] relative shadow-xl ${className}`}
            style={{ backgroundColor: "#C0C0DD" }}
        >
            <div className="flex flex-nowrap justify-between items-center gap-2">
                {/* WHEN? */}
                <div
                    ref={dateInputRef}
                    className="relative flex-1"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <SearchFormField
                        label="WHEN?"
                        value={date ? date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        }) : 'Select date'}
                        isEditing={dateEditing}
                        onClick={(e) => {
                            // Use a more robust approach to handle date picker clicks
                            console.log('Date field clicked');
                            if (e) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                            handleDateClick();
                        }}
                    >
                        {dateEditing && (
                            <DatePicker
                                date={date}
                                setDate={handleDateChange}
                                onClose={() => {
                                    console.log('Closing date picker');
                                    setTimeout(() => setDateEditing(false), 10);
                                }}
                            />
                        )}
                    </SearchFormField>
                </div>

                {/* TIME? */}
                <div className="relative flex-1">
                    <SearchFormField
                        label="TIME?"
                        value={time ? new Date(0, 0, 0, parseInt(time.split(':')[0]), parseInt(time.split(':')[1] || '0')).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'Select time'}
                        isEditing={timeEditing}
                        onClick={handleTimeClick}
                    >
                        {timeEditing && <TimePicker time={time} setTime={handleTimeChange} onClose={() => setTimeEditing(false)} />}
                    </SearchFormField>
                </div>

                {/* WHO? */}
                <div
                    ref={peopleInputRef}
                    className="relative flex-1"
                >
                    <SearchFormField
                        label="WHO?"
                        value={`${partySize} people`}
                        isEditing={peopleEditing}
                        onClick={handlePeopleClick}
                    >
                        {peopleEditing && <PartySizePicker partySize={partySize} setPartySize={handlePartySizeChange} onClose={() => setPeopleEditing(false)} />}
                    </SearchFormField>
                </div>

                {/* VENUE TYPE? */}
                <div
                    ref={eventTypeInputRef}
                    className="relative flex-1"
                    onClick={(e) => {
                        console.log('Div container for VENUE TYPE clicked');
                        // Prevent propagation so it doesn't interfere with SearchFormField's onClick
                        e.stopPropagation();
                        handleEventTypeClick();
                    }}
                >
                    <SearchFormField
                        label="VENUE TYPE?"
                        value={eventType ? (eventType.split(',').length > 2
                            ? `${eventType.split(',').slice(0, 2).join(', ')} +${eventType.split(',').length - 2}`
                            : eventType) : 'vibe of space'}
                        isEditing={eventTypeEditing}
                        onClick={() => {
                            console.log('SearchFormField for VENUE TYPE clicked');
                            handleEventTypeClick();
                        }}
                    >
                        {eventTypeEditing && <EventTypePicker eventType={eventType} setEventType={handleEventTypeChange} onClose={() => setEventTypeEditing(false)} />}
                    </SearchFormField>
                </div>

                {/* WHERE? */}
                <div
                    ref={locationInputRef}
                    className="relative flex-1"
                    onClick={(e) => {
                        console.log('Div container for WHERE clicked');
                        // Prevent propagation so it doesn't interfere with SearchFormField's onClick
                        e.stopPropagation();
                        handleLocationClick();
                    }}
                >
                    <SearchFormField
                        label="WHERE?"
                        value={location ?
                            (location.split(',').length > 2
                                ? `${location.split(',').slice(0, 2).join(', ')} +${location.split(',').length - 2}`
                                : location)
                            : 'neighborhood'}
                        isEditing={locationEditing}
                        onClick={() => {
                            console.log('SearchFormField for WHERE clicked');
                            handleLocationClick();
                        }}
                    >
                        {locationEditing && <LocationPicker location={location} setLocation={handleLocationChange} onClose={() => setLocationEditing(false)} />}
                    </SearchFormField>
                </div>

                {/* Search Button */}
                <div className="relative">
                    <button
                        type="submit"
                        className="bg-[#1C1C34] text-white h-14 px-4 rounded-full font-semibold transition-all hover:bg-[#2B2B52] flex items-center justify-center whitespace-nowrap min-w-[120px] font-['Inter']"
                        aria-label="Search for venues"
                    >
                        SEARCH
                    </button>
                </div>
            </div>
        </form>
    );
};

export default SearchPill; 